import { useQuery, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { GET_ONBOARDING_STATE } from 'src/api/queries/getOnboardingState';
import { COMPLETE_ONBOARDING_STEP } from 'src/api/mutations/completeOnboardingStep';
import { SET_ONBOARDING_BILLING_PLAN } from 'src/api/mutations/setOnboardingBillingPlan';
import { RESET_ONBOARDING } from 'src/api/mutations/resetOnboarding';
import { useAppToast } from 'src/components/sonner';
import { useAuthContext } from 'src/auth/hooks';
import { getSelectedCompany } from 'src/utils/company-storage';
import { useAppBootstrap } from 'src/contexts/app-bootstrap-context';
import { getOnboardingErrorMessage } from 'src/sections/Onboarding/onboarding-errors';

// ----------------------------------------------------------------------

export interface OnboardingState {
  isNew: boolean;
  currentStep: number;
  completedSteps: number[];
  billingPlan: 'monthly' | 'annual' | null;
}

interface UseOnboardingStateOptions {
  eager?: boolean;
}

export function useOnboardingState({ eager = false }: UseOnboardingStateOptions = {}) {
  const { authenticated } = useAuthContext();
  const selectedCompany = getSelectedCompany();

  // Check if we already have onboarding data from bootstrap
  const { onboardingState: bootstrapOnboarding, validationComplete } = useAppBootstrap();

  // Bootstrap is authoritative for completed onboarding; the live query only runs while
  // onboarding is in progress (so guards/stepper react to mutations) or in eager mode.
  const onboardingCompletedPerBootstrap = !!bootstrapOnboarding && !bootstrapOnboarding.isNew;
  const shouldFetch =
    authenticated &&
    !!selectedCompany &&
    (eager || (validationComplete && !onboardingCompletedPerBootstrap));

  const { data, loading, error, refetch } = useQuery(GET_ONBOARDING_STATE, {
    skip: !shouldFetch,
  });

  const queryOnboarding = data?.getOnboardingState;
  const hasResolvedOnboardingState = Boolean(queryOnboarding || bootstrapOnboarding);

  // Prefer live query state when available, then bootstrap fallback.
  const onboardingState: OnboardingState = queryOnboarding
    ? {
        isNew: queryOnboarding.isNew,
        currentStep: queryOnboarding.currentStep,
        completedSteps: queryOnboarding.completedSteps,
        billingPlan: queryOnboarding.billingPlan as 'monthly' | 'annual' | null,
      }
    : bootstrapOnboarding
      ? {
          isNew: bootstrapOnboarding.isNew,
          currentStep: bootstrapOnboarding.currentStep,
          completedSteps: bootstrapOnboarding.completedSteps,
          billingPlan: bootstrapOnboarding.billingPlan as 'monthly' | 'annual' | null,
        }
      : {
          // Placeholder, not a real step: a failed query lands here too, so callers must
          // check `error` before treating these defaults as the merchant's progress.
          isNew: true,
          currentStep: 0,
          completedSteps: [],
          billingPlan: null,
        };

  return {
    ...onboardingState,
    // Loading only blocks when we have no onboarding data from either source.
    // Eager mode is used by the onboarding route to avoid being blocked by bootstrap validation.
    loading:
      (!validationComplete && !eager) || (shouldFetch && loading && !hasResolvedOnboardingState),
    error,
    refetch,
  };
}

/**
 * Hook to complete an onboarding step
 * Refetches onboarding state after completion to get fresh data
 */
export function useCompleteStep() {
  const [completeStepMutation, { loading }] = useMutation(COMPLETE_ONBOARDING_STEP, {
    refetchQueries: [{ query: GET_ONBOARDING_STATE }],
    awaitRefetchQueries: true,
  });

  const completeStep = useCallback(
    async (step: number) => {
      try {
        const result = await completeStepMutation({
          variables: { step },
        });

        return result.data?.completeOnboardingStep?.currentStep ?? step + 1;
      } catch (error) {
        console.error('Error completing onboarding step:', error);
        throw new Error(
          getOnboardingErrorMessage(error, 'Failed to complete step. Please try again.')
        );
      }
    },
    [completeStepMutation]
  );

  return { completeStep, loading };
}

/**
 * Hook to set billing plan during onboarding
 */
export function useSetBillingPlan() {
  const { showToast } = useAppToast();

  const [setBillingPlanMutation, { loading }] = useMutation(SET_ONBOARDING_BILLING_PLAN, {
    refetchQueries: [{ query: GET_ONBOARDING_STATE }],
    awaitRefetchQueries: true,
  });

  const setBillingPlan = useCallback(
    async (plan: 'monthly' | 'annual') => {
      try {
        await setBillingPlanMutation({
          variables: { plan },
        });
      } catch (error) {
        console.error('Error setting billing plan:', error);
        showToast('Failed to set billing plan. Please try again.', 'error');
        throw error;
      }
    },
    [setBillingPlanMutation, showToast]
  );

  return { setBillingPlan, loading };
}

/**
 * Hook to reset onboarding (admin/dev function)
 */
export function useResetOnboarding() {
  const { showToast } = useAppToast();

  const [resetOnboardingMutation, { loading }] = useMutation(RESET_ONBOARDING, {
    refetchQueries: [{ query: GET_ONBOARDING_STATE }],
    awaitRefetchQueries: true,
  });

  const resetOnboarding = useCallback(async () => {
    try {
      await resetOnboardingMutation();
      showToast('Onboarding has been reset', 'success');
    } catch (error) {
      console.error('Error resetting onboarding:', error);
      showToast('Failed to reset onboarding. Please try again.', 'error');
      throw error;
    }
  }, [resetOnboardingMutation, showToast]);

  return { resetOnboarding, loading };
}
